import React, { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getFullUrl } from '../config';

// 3D Model Component for Carousel
function Interactive3DModel({ url, autoRotate = false }) {
  const { scene } = useGLTF(url);
  const clonedScene = scene.clone();
  const groupRef = useRef();

  React.useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useFrame((state, delta) => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.5;
    }
  });

  return (
    <group ref={groupRef}>
      <primitive object={clonedScene} scale={1.2} />
    </group>
  );
}

const ImageCarousel = ({ images, isOpen, onClose, initialIndex = 0, item = null, is3D = false }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const modalRef = useRef(null);

  // Prepare carousel items (3D model first if applicable, then images)
  const carouselItems = [];
  if (is3D && item) {
    carouselItems.push({ type: '3d', url: item.url, name: item.name });
  }
  if (images && images.length > 0) {
    carouselItems.push(...images.map(img => ({ type: 'image', url: img })));
  }

  const totalItems = carouselItems.length;

  // Reset to initial index when modal opens and force scroll to top
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Force modal to top position
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
        modalRef.current.scrollLeft = 0;
      }
      // Also force window scroll for extra safety
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
          modalRef.current.scrollLeft = 0;
        }
      }, 0);
    }
  }, [isOpen, initialIndex]);

  // Prevent body scroll and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll when modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    const originalPosition = window.getComputedStyle(document.body).position;
    const scrollY = window.scrollY;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      // Restore original body overflow and position
      document.body.style.overflow = originalStyle;
      document.body.style.position = originalPosition;
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
      // Remove event listeners
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentIndex, totalItems]);

  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => prev === 0 ? totalItems - 1 : prev - 1);
  }, [totalItems]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => prev === totalItems - 1 ? 0 : prev + 1);
  }, [totalItems]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };


  if (!isOpen || totalItems === 0) {
    return null;
  }

  const currentItem = carouselItems[currentIndex];
  const isCurrentItem3D = currentItem?.type === '3d';

  const modalContent = (
    <div
      ref={modalRef}
      className="fixed z-50 bg-black bg-opacity-90 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        touchAction: 'none',
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'touch',
        zIndex: 999999
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
        aria-label="Close carousel"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Navigation buttons - only show if more than one item */}
      {totalItems > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}

      {/* Main content area */}
      <div className="w-full h-full flex items-center justify-center p-4">
        {isCurrentItem3D ? (
          /* 3D Model Viewer */
          <div className="w-full h-full">
            <Canvas
              camera={{ position: [0, 0, 3], fov: 50 }}
              style={{ width: '100%', height: '100%' }}
              gl={{ preserveDrawingBuffer: false, antialias: true }}
            >
              <ambientLight intensity={0.6} />
              <directionalLight position={[10, 10, 5]} intensity={1} />
              <directionalLight position={[-10, -10, -5]} intensity={0.5} />
              <Suspense fallback={null}>
                <Interactive3DModel
                  url={getFullUrl(currentItem.url)}
                  autoRotate={false}
                />
                <OrbitControls
                  enablePan={true}
                  enableZoom={true}
                  enableRotate={true}
                  enableDamping={true}
                  dampingFactor={0.1}
                />
              </Suspense>
            </Canvas>
          </div>
        ) : (
          /* Image Viewer with Zoom (no swipe) */
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ mode: 'toggle' }}
            panning={{ disabled: false }}
            disabled={false}
          >
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%'
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src={getFullUrl(currentItem.url)}
                alt={`Image ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
                style={{ userSelect: 'none' }}
              />
            </TransformComponent>
          </TransformWrapper>
        )}
      </div>

      {/* Counter and dots - only show if more than one item */}
      {totalItems > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-black bg-opacity-50 rounded-full px-4 py-2 flex items-center gap-3">
            {/* Counter */}
            <span className="text-white text-sm font-medium">
              {currentIndex + 1} / {totalItems}
            </span>

            {/* Dots - show only if 10 or fewer items */}
            {totalItems <= 10 && (
              <div className="flex gap-1">
                {carouselItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-40 hover:bg-opacity-60'
                    } ${item.type === '3d' ? 'border border-blue-400' : ''}`}
                    aria-label={`Go to ${item.type === '3d' ? '3D model' : `image ${index + 1}`}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );

  // Render modal using portal to document.body to escape parent scroll contexts
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ImageCarousel;